import React from 'react';
import { MapPin, Phone, Mail, Building2, CheckCircle2, XCircle, ExternalLink } from 'lucide-react';

/**
 * AgentInfoBanner component for displaying agent information in a banner
 * @param {Object} props - Component props
 * @param {Object} props.agentInfo - Agent info data
 * @returns {JSX.Element} AgentInfoBanner component
 */
const AgentInfoBanner = ({ agentInfo }) => {
  if (!agentInfo) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center">
        <p className="text-gray-500">No agent information available</p>
      </div>
    );
  }

  const {
    id,
    name,
    email,
    phone,
    countryCode,
    isActive,
    logo,
    address,
    googleMapUrl,
    callOut,
    taxInformation,
    services,
    metadata
  } = agentInfo;

  const formatPhone = () => {
    if (!phone) return 'N/A';
    const code = countryCode || '';
    return `${code} ${phone}`;
  };

  const formatAddress = () => {
    if (!address) return 'N/A';
    const parts = [
      address.address_line,
      address.city,
      address.state,
      address.countryCode
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  const serviceLabels = {
    flight_alerts: 'Flight Alerts',
    post_booking_services: 'Post Booking',
    lounges: 'Lounges',
    auto_webcheckin: 'Auto Web Check-in',
    esim: 'eSIM',
    travel_insurance: 'Travel Insurance'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div className="flex items-start gap-4">
          {/* Logo */}
          <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
            {logo ? (
              <img src={logo} alt={name} className="w-full h-full object-cover" />
            ) : (
              <Building2 className="w-10 h-10 text-gray-400" />
            )}
          </div>

          {/* Basic Info */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold text-gray-900">{name || 'N/A'}</h1>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  isActive
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <p className="text-sm text-gray-500 mb-2">Agent ID: {id}</p>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700">{email || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700">{formatPhone()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Call Out */}
        {callOut && (
          <div className="bg-blue-50 px-4 py-2 rounded-lg">
            <p className="text-sm text-blue-800 font-medium">{callOut}</p>
          </div>
        )}
      </div>

      {/* Address & Map */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-4 h-4 text-gray-500" />
            <span className="font-medium text-gray-900">Address</span>
          </div>
          <p className="text-sm text-gray-700">{formatAddress()}</p>
          {googleMapUrl && (
            <a
              href={googleMapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 hover:text-blue-800"
            >
              <ExternalLink className="w-3 h-3" />
              View on Google Maps
            </a>
          )}
        </div>

        {/* Tax Information */}
        {taxInformation && (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-gray-500" />
              <span className="font-medium text-gray-900">Tax Information</span>
            </div>
            <p className="text-sm text-gray-700">
              <span className="text-gray-500">Tax Number:</span> {taxInformation.tax_number || 'N/A'}
            </p>
            <p className="text-sm text-gray-700">
              <span className="text-gray-500">Country:</span> {taxInformation.countryCode || 'N/A'}
            </p>
          </div>
        )}
      </div>

      {/* Services */}
      {services && services.length > 0 && (
        <div className="border-t border-gray-100 pt-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Services</h3>
          <div className="flex flex-wrap gap-2">
            {services.map((service) => (
              <div
                key={service.code}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                  service.active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {service.active ? (
                  <CheckCircle2 className="w-3 h-3" />
                ) : (
                  <XCircle className="w-3 h-3" />
                )}
                {serviceLabels[service.code] || service.code}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metadata */}
      {metadata && (
        <div className="border-t border-gray-100 pt-4 mt-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Preferences</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {metadata.currency_preference && (
              <div className="text-sm">
                <span className="text-gray-500">Currency:</span>{' '}
                <span className="text-gray-700 font-medium">{metadata.currency_preference}</span>
              </div>
            )}
            {metadata.seat_preference && (
              <div className="text-sm">
                <span className="text-gray-500">Seat Preference:</span>{' '}
                <span className="text-gray-700 font-medium">
                  {Object.keys(metadata.seat_preference).length} passenger types configured
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentInfoBanner;
